import React, { useState, useEffect } from "react";
import GraphCanvas from "./GraphCanvas";
import LayerExecutionCanvas from "./LayerExecutionCanvas";
import { supabase } from "../../services/supabase";
import { parseMXNetGraph } from "../../lib/mxnetParser";

const NetronVisualizer = ({ modelId, sessionId, refreshTrigger, onNodeSelect }) => {
  const [activeTab, setActiveTab] = useState("layers");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modelData, setModelData] = useState(null);
  const [layerGraphData, setLayerGraphData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Listen to external refresh trigger
  useEffect(() => {
    if (refreshTrigger) {
      console.log('[NetronVisualizer] Refresh triggered, incrementing refresh key');
      setRefreshKey(prev => prev + 1);
    }
  }, [refreshTrigger]);

  // Fetch model graph data
  useEffect(() => {
    const fetchGraphData = async () => {
      if (!modelId) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch model graph from Supabase
        const { data: modelGraphData, error: modelError } = await supabase
          .from('models')
          .select('name, model_graph')
          .eq('id', modelId)
          .single();

        if (modelError) {
          throw new Error(`Failed to fetch model: ${modelError.message}`);
        }

        if (!modelGraphData || !modelGraphData.model_graph) {
          throw new Error('Model graph data not found');
        }

        // Parse the raw MXNet graph into Netron format
        const parsedModel = parseMXNetGraph(modelGraphData.model_graph, modelGraphData.name || 'Model');

        console.log('Parsed model:', parsedModel);

        // Attach raw MXNet graph data for sidebar access
        parsedModel.rawGraph = modelGraphData.model_graph;

        setModelData(parsedModel);
      } catch (err) {
        console.error('Error loading model from Supabase:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGraphData();
  }, [modelId, refreshKey]);

  // Fetch layer execution graph data from user_session_graphs
  useEffect(() => {
    const fetchLayerGraph = async () => {
      if (!sessionId) return;

      try {
        // Fetch pre-generated graph from user_session_graphs
        const { data: graphData, error: graphError } = await supabase
          .from('user_session_graphs')
          .select('content')
          .eq('id', sessionId)
          .single();

        if (graphError) {
          console.error('Error fetching session graph:', graphError);
          return;
        }

        if (!graphData || !graphData.content) {
          console.log('No session graph found - graph may not be generated yet');
          return;
        }

        // The content is already in MXNet format from visualizer_service
        // Store the raw MXNet graph directly for LayerExecutionCanvas
        console.log('Layer execution graph loaded from database:', graphData.content);
        setLayerGraphData(graphData.content);
      } catch (err) {
        console.error('Error loading session graph:', err);
      }
    };

    fetchLayerGraph();
  }, [sessionId, refreshKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-lg text-gray-600">Loading model graph...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg">
          <p className="text-red-800 text-sm">
            <strong>Error:</strong> {error}
          </p>
        </div>
      </div>
    );
  }

  // Determine which graph to display based on active tab
  const hasModelGraph = modelData && modelData.graphs && modelData.graphs.length > 0;
  const hasLayerGraph = layerGraphData && layerGraphData.nodes && layerGraphData.nodes.length > 0;

  return (
    <div className="flex flex-col w-full h-full">
      {/* Tab Navigation */}
      <div className="flex items-center justify-between border-b border-gray-200 mb-4 flex-shrink-0 bg-white">
        <div className="flex">
          <button
            onClick={() => setActiveTab("layers")}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === "layers"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Layer Graph
          </button>
          <button
            onClick={() => setActiveTab("model")}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === "model"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Model Graph
          </button>
        </div>
      </div>

      {/* Graph Viewer */}
      <div className="flex-1 w-full overflow-hidden">
        {activeTab === "layers" ? (
          hasLayerGraph ? (
            <LayerExecutionCanvas graph={layerGraphData} onNodeSelect={onNodeSelect} />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-100">
              <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <p className="text-gray-500">No layer execution data available</p>
              </div>
            </div>
          )
        ) : (
          hasModelGraph ? (
            <GraphCanvas graph={modelData.graphs[0]} onNodeSelect={onNodeSelect} />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-100">
              <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <p className="text-gray-500">No model graph data available</p>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default NetronVisualizer;
